import { useStrapiParams } from '@/hooks';
import type { PopulateParam, StrapiListRequest, FilterConfig, FiltersState } from '@/interfaces/strapi';

type DropdownFilter = Extract<FilterConfig, { type: 'dropdown' }>;
type DateFilter = Extract<FilterConfig, { type: 'date' }>;
type SearchFilter = Extract<FilterConfig, { type: 'search' }>;

const isDropdownFilter = (f: FilterConfig): f is DropdownFilter => f.type === 'dropdown';
const isDateFilter = (f: FilterConfig): f is DateFilter => f.type === 'date';
const isSearchFilter = (f: FilterConfig): f is SearchFilter => f.type === 'search';

export const useStrapiFilters = ({
	page,
	pageSize,
	sort,
	populate,
	filters,
	filterConfig,
	staticFilters, // <- nuevo
}: {
	page?: number;
	pageSize?: number;
	sort?: string;
	populate?: PopulateParam;
	filters?: FiltersState;
	filterConfig?: FilterConfig[];
	staticFilters?: Record<string, unknown>;
}): StrapiListRequest => {
	const filtersObject: Record<string, unknown> = {};

	// ---- SEARCH
	const searchCfg = filterConfig?.find(isSearchFilter);
	const search = filters?.search?.trim();
	if (search && searchCfg) {
		const { field, subField } = searchCfg;
		if (subField) {
			filtersObject[field] = { [subField]: { $containsi: search } };
		} else {
			filtersObject[field] = { $containsi: search };
		}
	}

	// ---- DATE RANGE
	const dateField = filterConfig?.find(isDateFilter)?.field;
	const from = filters?.dateRange?.from;
	const to = filters?.dateRange?.to;
	if (dateField && (from ?? to)) {
		filtersObject[dateField] = {
			...(from && { $gte: from.toISOString() }),
			...(to && { $lte: to.toISOString() }),
		};
	}

	// ---- DROPDOWNS
	const dropdownFilters =
		filterConfig?.filter(isDropdownFilter).reduce<Record<string, unknown>>((acc, curr) => {
			const value = filters?.dropdowns?.[curr.field];
			if (value !== undefined && value !== null && value !== '') {
				acc[curr.field] = { documentId: { $eq: value } };
			}
			return acc;
		}, {}) ?? {};

	// ---- COMPOSE (staticFilters tiene prioridad final)
	const composed = { ...filtersObject, ...dropdownFilters, ...(staticFilters ?? {}) };
	const additionalFilters = Object.keys(composed).length > 0 ? composed : undefined;

	return useStrapiParams({
		page,
		pageSize,
		sort,
		populate,
		additionalFilters,
	});
};
